
import React, { useState } from 'react';
import { Card, Avatar, Typography, Descriptions, Button, Modal, Form, Input, Upload, message, Tooltip, Row, Col, Divider } from 'antd';
import { EditOutlined, UploadOutlined } from '@ant-design/icons';
import { RcFile, UploadChangeParam, UploadFile } from 'antd/es/upload/interface';
import { User } from '../../types';
import api from '../../services/api';
import './ProfileSection.css';

const { Title, Text } = Typography;

interface ProfileSectionProps {
  user: User;
  onUpdate: (updatedUser: User) => void;
}

const ProfileSection: React.FC<ProfileSectionProps> = ({ user, onUpdate }) => {
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [form] = Form.useForm();
  const [editingField, setEditingField] = useState<string | null>(null);
  const [inlineLoading, setInlineLoading] = useState<boolean>(false);

  const showModal = () => {
    setIsModalVisible(true);
    form.setFieldsValue({
      firstName: user.firstName,
      middleName: user.middleName,
      lastName: user.lastName,
      suffix: user.suffix,
      prefix: user.prefix,
      email: user.email,
      phone: user.phone, 
      gender: user.sex,
      dob: user.dob,
      maritalStatus: user.maritalStatus,
      roles: user.roles,
      userType: user.userType,
    });
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setFileList([]);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const updatedUser: User = { ...user, ...values };

      // Handle profile picture upload if a new file is selected
      if (fileList.length > 0 && fileList[0].originFileObj) {
        const formData = new FormData();
        formData.append('profilePic', fileList[0].originFileObj as RcFile);

        // Use the bulk update endpoint to handle file upload and profile update
        const response = await api.patch('/user/me/profile', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response.status === 200) {
          const updatedData = response.data.user; // Adjust based on your response
          onUpdate(updatedData);
          message.success('Profile picture updated successfully.');
        } else {
          throw new Error('Failed to upload profile picture.');
        }
      }

      // Update user profile via API
      const updateResponse = await api.patch('/user/me/profile', updatedUser, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (updateResponse.status === 200) {
        const updatedData = updateResponse.data.user; // Adjust based on your response structure
        onUpdate(updatedData);
        message.success('Profile updated successfully.');
        setIsModalVisible(false);
        setFileList([]);
      } else {
        throw new Error('Failed to update profile.');
      }
    } catch (error: any) {
      console.error(error);
      message.error(error.message || 'An error occurred while updating the profile.');
    }
  };

  const handleChange = (info: UploadChangeParam<UploadFile>) => {
    setFileList(info.fileList.slice(-1)); // Keep only the latest file
  };

  const handleInlineEdit = async (field: string, value: string) => {
    setEditingField(field);
    setInlineLoading(true);
    try {
      const payload = { [field]: value };

      const response = await api.patch('/user/me/profile', payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200) {
        const updatedUser = response.data.user;
        onUpdate(updatedUser);
        message.success(`${capitalizeFirstLetter(field)} updated successfully.`);
      } else {
        throw new Error(`Failed to update ${field}.`);
      }
    } catch (error: any) {
      console.error(error);
      message.error(error.response?.data?.message || `An error occurred while updating ${field}.`);
    } finally {
      setEditingField(null);
      setInlineLoading(false);
    }
  };

  const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  let editableFields = [
    { label: 'Email', field: 'email', value: user.email },
    { label: 'Phone', field: 'phone', value: user.phone || 'N/A' },
    // { label: 'Prefix', field: 'prefix', value: user.prefix || 'N/A' },
    // { label: 'Suffix', field: 'suffix', value: user.suffix || 'N/A' },
    // { label: 'Gender', field: 'sex', value: user.sex || 'N/A' },
    // { label: 'Date of Birth', field: 'dob', value: user.dob || 'N/A' },
    // { label: 'Marital Status', field: 'maritalStatus', value: user.maritalStatus || 'N/A' },
    ];

  // If user.roles is admin allow editing roles and userType
  if (user.roles.includes('admin')) {
    editableFields = [
      { label: 'Roles', field: 'roles', value: user.roles || 'N/A' },
      // { label: 'User Type', field: 'userType', value: user.userType || 'N/A' },
      ...editableFields,
      
    ];
  }

  return (
    <Card
      title="Profile Information"
      bordered={false}
      className="profile-card"
      bodyStyle={{ padding: '2rem' }}
    >
      <div className="profile-header">
        <Avatar
          size={120}
          src={user.profilePic || 'https://www.gravatar.com/avatar/?d=mp&f=y'}
          className="profile-avatar"
        />
        <div className="profile-details">
          <Title level={4} className="profile-name">
            {`${user.prefix ? user.prefix + ' ' : ''}${user.firstName} ${user.middleName ? user.middleName + ' ' : ''}${user.lastName || ''} ${user.suffix || ''}`}
          </Title>
          <Text type="secondary" className="profile-type">
            {user.userType || 'User'}
          </Text>
        </div>
      </div>

      <Divider />

      <Descriptions
        bordered
        column={1}
        size="middle"
        layout="vertical"
        className="profile-descriptions"
      >
        {editableFields.map(({ label, field, value }) => (
          <Descriptions.Item key={field} label={label}>
            <Typography.Text
              editable={{
                onChange: (val) => handleInlineEdit(field, val),
                icon: <EditOutlined />,
                tooltip: `Edit ${label}`,
              }}
              style={{ cursor: 'pointer' }}
            >
              {value}
            </Typography.Text>
          </Descriptions.Item>
        ))}

        {/* Non-editable Fields */}
        <Descriptions.Item label="Gender">
          <Text>{user.sex || 'N/A'}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Date of Birth">
          <Text>{user.dob ? new Date(user.dob).toLocaleDateString() : 'N/A'}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Marital Status">
          <Text>{user.maritalStatus || 'N/A'}</Text>
        </Descriptions.Item>
        {/* <Descriptions.Item label="Roles">
          <Text>{user.roles.replace(/,/g, ', ')}</Text>
        </Descriptions.Item> */}
      </Descriptions>

      {/* Bulk Edit Button */}
      <div style={{ textAlign: 'right', marginTop: '1rem' }}>
        <Button type="primary" icon={<EditOutlined />} onClick={showModal} className="edit-button">
          Edit Profile
        </Button>
      </div>

      {/* Edit Profile Modal */}
      <Modal
        title="Edit Profile"
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Save Changes"
        cancelText="Cancel"
        width={600}
        centered
      >
        <Form
          form={form}
          layout="vertical"
          name="edit_profile_form"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[{ required: true, message: 'Please enter your first name.' }]}
              >
                <Input placeholder="First Name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="middleName"
                label="Middle Name"
              >
                <Input placeholder="Middle Name" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Last Name"
              >
                <Input placeholder="Last Name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="suffix"
                label="Suffix"
              >
                <Input placeholder="Suffix" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { 
                    required: true, 
                    message: 'Please enter your email.' 
                  },
                  { 
                    type: 'email', 
                    message: 'Please enter a valid email.' 
                  }
                ]}
              >
                <Input placeholder="Email" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone"
              >
                <Input placeholder="Phone Number" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="sex"
                label="Gender"
              >
                <Input placeholder="Gender" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="dob"
                label="Date of Birth"
              >
                <Input type="date" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="maritalStatus"
                label="Marital Status"
              >
                <Input placeholder="Marital Status" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="userType"
                label="User Type"
              >
                <Input placeholder="User Type" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Profile Picture"
          >
            <Upload
              beforeUpload={() => false} // Prevent automatic upload
              onChange={handleChange}
              fileList={fileList}
              maxCount={1}
              accept=".png,.jpg,.jpeg,.gif"
            >
              <Button icon={<UploadOutlined />}>Select New Picture</Button>
            </Upload>
            <div style={{ marginTop: '0.5rem' }}>
              <span>Allowed file types: PNG, JPG, JPEG, GIF. Max size: 5MB.</span>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ProfileSection;
